import Button from '@ixigo/iui/ButtonBase';
import Typography from '@ixigo/iui/Typography';
import type { ReactNode } from 'react';

export interface DisplayFieldProps {
	icon?: ReactNode;
	value?: string;
	placeholder?: string;
	onClick?: () => void;
	className?: string;
}

/**
 * A component to display data that looks like a form field but is not an input.
 * It's a button that can be clicked to initiate an action, like opening a modal.
 * Based on the Figma component "Search Field data".
 *
 * @param {object} props - The component props.
 * @param {ReactNode} [props.icon] - An icon to display on the left.
 * @param {string} [props.value] - The value to display. If empty, the placeholder is shown.
 * @param {string} [props.placeholder="Select"] - The placeholder text to show when there is no value.
 * @param {() => void} [props.onClick] - The function to call when the component is clicked.
 * @param {string} [props.className] - Additional CSS classes to apply to the button.
 * @returns {JSX.Element} The rendered component.
 */
export function DisplayField({
	icon,
	value,
	placeholder = 'Select',
	onClick,
	className = '',
}: DisplayFieldProps) {
	const hasValue = value && value.trim() !== '';

	return (
		<Button
			onClick={onClick}
			// Todo: outline issue in button component when focus:visible is active
			className={`flex items-center outline-none gap-2 w-full h-50px px-4 text-left justify-start ${className}`}
		>
			{icon && (
				<div
					className={`shrink-0 w-6 h-6 flex items-center justify-center ${
						hasValue ? 'text-primary' : 'text-secondary'
					}`}
				>
					{icon}
				</div>
			)}
			<Typography
				className={`font-medium flex-1 truncate ${
					hasValue ? 'text-primary' : 'text-secondary'
				}`}
				variant="body-md"
			>
				{hasValue ? value : placeholder}
			</Typography>
		</Button>
	);
}
